import React, { useEffect, useState, useRef } from "react";
import "../ExerciseTestStepper/ExerciseTestStepper.scss";
import { Link, useNavigate } from "react-router-dom";
import Logo from "../../assets/images/logo/header-logo.svg";
import EmptyStates from "../../assets/images/banner/EmptyStates.svg";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { LightButton, PrimaryButton } from "../AllButtons/AllButtons";
import { RootState, useAppDispatch, useAppSelector } from "../../Store/store";
import { generateMcqs, submitMcqs } from "../../Services/mcq.service";
import McqLoader from "../Loader/McqLoader/McqLoader";
import MathRenderer from "../CommonElements/MathRenderer";
import { getUserConversations } from "../../Services/chatbox.service";
import { getUserDetails } from "../../Services/profile.service";
import ButtonLoader from "../Loader/Loader/ButtonLoader";
import { openSnackbar } from "../../Slice/snackbarSlice";

interface Mcq {
  id: string;
  question: string;
  options: Record<string, string>;
}

interface OpenEndedQuestion {
  id: string;
  question: string;
}
const ANSWER_REQUIRED_ERROR = "Please answer the question before proceeding.";
const BLANK_SPACE_ERROR = "Blank spaces are not allowed.";
const MULTIPLE_SPACE_ERROR = "Blank spaces are not allowed";

// Remove the local mathJaxConfig as it's now in MathRenderer
const ExerciseTestStepper: React.FC = () => {
  const user = useAppSelector((state: RootState)=>state.user.user);
  const isLoading = useAppSelector((state) => state.mcq.loading);
  const [currentStep, setCurrentStep] = useState(1);
  const [mcqs, setMcqs] = useState<Mcq[]>([]);
  const [mcqAnswers, setMcqAnswers] = useState<Record<string, string>>({});
  const [openEndedQue, setOpenEndedQue] = useState<OpenEndedQuestion[]>([]);
  const [openEndedAnswers, setOpenEndedAnswers] = useState<Record<string, string>>({});
  // Refs to always have latest answers
  const mcqAnswersRef = useRef(mcqAnswers);
  const openEndedAnswersRef = useRef(openEndedAnswers);
    // Keep refs in sync with state
    useEffect(() => { mcqAnswersRef.current = mcqAnswers; }, [mcqAnswers]);
    useEffect(() => { openEndedAnswersRef.current = openEndedAnswers; }, [openEndedAnswers]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [isStudyRelated, setIsStudyRelated] = useState<any>(true);
  const [timeLeft, setTimeLeft] = useState(600);
  const conversationId = useAppSelector(state => state.chatai.latestConversationId);
  const [stepError, setStepError] = useState(false);
  const [stepErrorMessage, setStepErrorMessage] = useState(ANSWER_REQUIRED_ERROR);
  const [disableButton, setDisableButton] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isBlankSpaceAnswer = (answer: string = "") => {
    return answer.length > 0 && answer.trim().length === 0;
  };

  const hasMultipleSpaces = (answer: string = "") => {
    return / {2,}/.test(answer);
  };

  useEffect(() => {
    dispatch(getUserDetails(localStorage.getItem('id')));
    const handleConversationFetch = async () => {
      try {
        await dispatch(getUserConversations(localStorage.getItem("id"))).unwrap();
      } catch (error) {
        console.error("Error fetching conversations", error);
      }
    };
    handleConversationFetch();
  }, []);

  useEffect(() => {
    setTotalQuestions(mcqs.length + openEndedQue.length);
  }, [mcqs, openEndedQue]);

  useEffect(() => {
    const enterFullscreen = async () => {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (err) {
        console.error('Failed to enter fullscreen:', err);
      }
    };

    // enterFullscreen();

    // Add a listener for fullscreenchange event
    const handleFullscreenChange = () => {
      // If fullscreen was exited, try to re-enter it
      if (!document.fullscreenElement) {
        enterFullscreen();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    const blockNavigation = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', blockNavigation);

    const blockShortcuts = (e: KeyboardEvent) => {
      // Prevent F5, Ctrl+R, Ctrl+Shift+R, Ctrl+W, and Alt+F4
      if (
        e.key === 'F5' ||
        (e.ctrlKey && e.key === 'r') ||
        (e.ctrlKey && e.shiftKey && e.key === 'R') ||
        (e.ctrlKey && e.key === 'w') ||
        (e.altKey && e.key === 'F4')
      ) {
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', blockShortcuts);

    return () => {
      window.removeEventListener('beforeunload', blockNavigation);
      window.removeEventListener('keydown', blockShortcuts);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);

      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(err => console.error(err));
      }
    };
  }, []);


  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secondsLeft = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(secondsLeft).padStart(2, "0")}`;
  };



  const updateProgressBar = () => {
    const progressPercentage = ((currentStep - 1) / (totalQuestions - 1)) * 100;
    const progressBar = document.querySelector(".progress-bar");
    if (progressBar) {
      progressBar.setAttribute("style", `width: ${progressPercentage}%`);
    }
  };

  // const displayStep = (stepNumber: number) => {
  //   if (stepNumber >= 1 && stepNumber <= totalQuestions) {
  //     setCurrentStep(stepNumber);
  //     updateProgressBar();
  //   }
  // };

  const nextStep = () => {
    let answered = false;
    if (currentStep <= mcqs.length) {
      // MCQ
      const mcq = mcqs[currentStep - 1];
      answered = !!mcqAnswers[mcq?.id];
    } else {
      // Open-ended
      const openIndex = currentStep - mcqs.length - 1;
      const que = openEndedQue[openIndex];
      const answer = openEndedAnswers[que?.id] || "";
      if (isBlankSpaceAnswer(answer)) {
        setStepErrorMessage(BLANK_SPACE_ERROR);
        setStepError(true);
        return;
      }
      if (hasMultipleSpaces(answer)) {
        setStepErrorMessage(MULTIPLE_SPACE_ERROR);
        setStepError(true);
        return;
      }
      answered = !!answer.trim();
    }

    if (answered) {
      setStepError(false);
      setStepErrorMessage(ANSWER_REQUIRED_ERROR);
      if (currentStep < totalQuestions) {
        setCurrentStep((prev) => prev + 1);
        updateProgressBar();
      }
    } else {
      setStepErrorMessage(ANSWER_REQUIRED_ERROR);
      setStepError(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      updateProgressBar();
    }
  };

  const dispatch = useAppDispatch();
  // const subjectId = useAppSelector(state => state.chatai.latestSubjectId);
  
  const userId = useAppSelector(state => state.user.uid);
  const navigate = useNavigate();

  const requestBody = {
    user_id: userId,
    conversation_id: localStorage.getItem("conver_ID") || conversationId,
  };
  const mcqAnsToBeSent = Object.entries(mcqAnswers).map(([questionId, selectedOption]) => ({
    question_id: questionId,
    selected_option: selectedOption,
  }));

  const openEndedToBeSent = Object.entries(openEndedAnswers).map(([id, question]) => ({
    question_id: id,
    selected_option: question
  }))
  const combinedAnswers = [...mcqAnsToBeSent, ...openEndedToBeSent];
  const requestBodyForSubmitting = {
    user_id: userId,
    answers: combinedAnswers
  }

  useEffect(() => {
    dispatch(generateMcqs(requestBody))
      .unwrap()
      .then((response) => {
        let fetchedMcqs: Mcq[] = [];
        let fetchedOpenEnded: OpenEndedQuestion[] = [];
        if (response.message === 'No study-related prompts found') {
          setIsStudyRelated(false);
        } else {
          fetchedMcqs = response.data.mcqs || [];
          fetchedOpenEnded = response.data.open_ended_questions || [];
          setMcqs(fetchedMcqs);
          setOpenEndedQue(fetchedOpenEnded);
          setIsStudyRelated(true);
        }
        const timer = setInterval(() => {
          setTimeLeft((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              setDisableButton(true);
              // Use refs to get latest answers and ensure all questions are included
              const mcqAnsToBeSent = fetchedMcqs.map((mcq) => ({
                question_id: mcq.id,
                selected_option: mcqAnswersRef.current[mcq.id] || "",
              }));
              const openEndedToBeSent = fetchedOpenEnded.map((que) => ({
                question_id: que.id,
                selected_option: openEndedAnswersRef.current[que.id] || "",
              }));
              const combinedAnswers = [...mcqAnsToBeSent, ...openEndedToBeSent];
              const autoRequestBody = {
                user_id: userId,
                answers: combinedAnswers
              };
              dispatch(submitMcqs(autoRequestBody))
                .unwrap()
                .then((response) => {
                  if (user.remaining_tokens <= 0) {
                    navigate("/price");
                  } else {
                    navigate("/dashboard/test_score", { replace: true });
                  }
                })
                .catch((error) => {
                  console.error("Error submitting MCQs automatically:", error);
                  dispatch(openSnackbar({
                    message: "Something went wrong. Please try again",
                    severity: "error"
                  }));
                });
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        return () => clearInterval(timer);

      })
      .catch((error) => {
        console.error("Error fetching MCQs:", error);
        setDisableButton(false);
        dispatch(openSnackbar({
          message: "Something went wrong. Please try again",
          severity: "error"
        }));
      });
  }, [dispatch]);



  if (isLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      }}>
        <McqLoader />
      </div>
    );
  }


  const handleSubmit = () => {
    let answered = false;
    if (currentStep <= mcqs.length) {
      // MCQ
      const mcq = mcqs[currentStep - 1];
      answered = !!mcqAnswers[mcq?.id];
    } else {
      // Open-ended
      const openIndex = currentStep - mcqs.length - 1;
      const que = openEndedQue[openIndex];
      const answer = openEndedAnswers[que?.id] || "";
      if (isBlankSpaceAnswer(answer)) {
        setStepErrorMessage(BLANK_SPACE_ERROR);
        setStepError(true);
        return;
      }
      if (hasMultipleSpaces(answer)) {
        setStepErrorMessage(MULTIPLE_SPACE_ERROR);
        setStepError(true);
        return;
      }
      answered = !!answer.trim();
    }

    if (!answered) {
      setStepErrorMessage(ANSWER_REQUIRED_ERROR);
      setStepError(true);
      return;
    } else {
      setStepError(false);
      setStepErrorMessage(ANSWER_REQUIRED_ERROR);
      setIsSubmitting(true);
      setDisableButton(true);
    }

    dispatch(submitMcqs(requestBodyForSubmitting))
      .unwrap()
      .then((response) => {
        if (user.remaining_tokens <= 0) {
          navigate("/price");
        } else {
          navigate("/dashboard/test_score", { replace: true });
        }
      })
      .catch((error) => {
        console.error("Error submitting MCQs:", error);
        setDisableButton(false);
        dispatch(openSnackbar({
          message: "Something went wrong. Please try again",
          severity: "error"
        }));
      });
  }

  const progressPercentage = isSubmitting
    ? 100
    : Math.floor(((currentStep - 1) / totalQuestions) * 100);

  return (
    <div className="exercise-test-section">
      <div className="test-stepper-wrapper">
        {isStudyRelated && <div className="test-stepper-header">
          <Link to="/">
            <img src={Logo} alt="" />
          </Link>
          <div className="timer-count ">{formatTime(timeLeft)}</div>
        </div>}

        <div className="test-stepper-content-body">
          <div className="test-stepper-container" id="container">
            {isStudyRelated && <div className="progress-label">
              <span>Question {currentStep} of {totalQuestions}</span>
              <span>{progressPercentage}%</span>
            </div>}
            {isStudyRelated && <div className="progress" style={{ height: "8px" }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${progressPercentage}%` }}
                aria-valuenow={currentStep}
                aria-valuemin={1}
                aria-valuemax={totalQuestions}
              ></div>
            </div>}

            <form className="" id="multi-step-form" onSubmit={(e) => { e.preventDefault() }}>
              {mcqs.map((mcq, index) => (
                <div
                  key={`mcq-${mcq.id || index}`}
                  className={`step step-${index + 1} ${currentStep === index + 1 ? "d-block" : "d-none"}`}
                >
                  <div className="mb-3">
                    <div className="stepper-form-block">
                      <FormControl>
                        <FormLabel
                          className="stepper-question"
                          id={`question-label-${index + 1}`}
                        >
                          <MathRenderer text={`${index + 1}. ${mcq.question}`} />
                        </FormLabel>
                        <RadioGroup
                          aria-labelledby={`question-label-${index + 1}`}
                          name={`radio-buttons-group-${index + 1}`}
                          onChange={(e) => {
                            const numberToLetterMap = ['A', 'B', 'C', 'D'];
                            const selectedAnswerIndex = parseInt(e.target.value, 10);
                            const selectedAnswer = numberToLetterMap[selectedAnswerIndex];
                            setMcqAnswers((prev) => ({
                              ...prev,
                              [mcq.id]: selectedAnswer,
                            }));
                            setStepError(false);
                            setStepErrorMessage(ANSWER_REQUIRED_ERROR);
                          }}
                        >
                          {Object.entries(mcq.options).map(([option, text]) => (
                            <FormControlLabel
                              key={option}
                              value={option}
                              control={<Radio />}
                              label={<MathRenderer text={`${text}`} />}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </div>
                  </div>
                  <div className="stepper-form-action">
                    <LightButton onClick={prevStep} disabled={currentStep === 1}>
                      <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg" version="1.1" transform="matrix(-1,0,0,1,0,0)">
                        <path d="M13.7063 6.70664C14.0969 6.31602 14.0969 5.68164 13.7063 5.29102L8.70625 0.291016C8.31563 -0.0996094 7.68125 -0.0996094 7.29063 0.291016C6.9 0.681641 6.9 1.31602 7.29063 1.70664L10.5875 5.00039H1C0.446875 5.00039 0 5.44727 0 6.00039C0 6.55352 0.446875 7.00039 1 7.00039H10.5844L7.29375 10.2941C6.90312 10.6848 6.90312 11.3191 7.29375 11.7098C7.68437 12.1004 8.31875 12.1004 8.70938 11.7098L13.7094 6.70977L13.7063 6.70664Z" fill="white"></path>
                      </svg>
                      Previous
                    </LightButton>

                    {stepError && (
                      <div className="step-error" style={{ color: 'red', marginBottom: 8 }}>
                        {stepErrorMessage}
                      </div>
                    )}

                    <PrimaryButton className="next-button" onClick={nextStep}>
                      Next
                      <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13.7063 6.70664C14.0969 6.31602 14.0969 5.68164 13.7063 5.29102L8.70625 0.291016C8.31563 -0.0996094 7.68125 -0.0996094 7.29063 0.291016C6.9 0.681641 6.9 1.31602 7.29063 1.70664L10.5875 5.00039H1C0.446875 5.00039 0 5.44727 0 6.00039C0 6.55352 0.446875 7.00039 1 7.00039H10.5844L7.29375 10.2941C6.90312 10.6848 6.90312 11.3191 7.29375 11.7098C7.68437 12.1004 8.31875 12.1004 8.70938 11.7098L13.7094 6.70977L13.7063 6.70664Z" fill="white" />
                      </svg>
                    </PrimaryButton>

                  </div>
                </div>
              ))}


              {openEndedQue.map((que, index) => {
                const questionNumber = mcqs.length + index + 1;
                return (
                  <div
                    key={`open-ended-${index}`}
                    className={`step step-${questionNumber} ${currentStep === questionNumber ? "d-block" : "d-none"}`}
                  >
                    <div className="mb-3">
                      <div className="stepper-form-block">
                        <FormControl>
                          <FormLabel
                            className="stepper-question"
                            id={`question-label-${questionNumber}`}
                          >
                            <MathRenderer text={`${questionNumber}. ${que.question}`} />
                          </FormLabel>
                          <div className="test-text-area">
                            <textarea
                              className="form-control text-area-editor"
                              placeholder="Enter your answer here..."
                              rows={6}
                              onChange={(e) => {
                                const newAnswer = e.target.value;
                                setOpenEndedAnswers((prev) => ({
                                  ...prev,
                                  [que.id]: newAnswer,
                                }));
                                if (isBlankSpaceAnswer(newAnswer)) {
                                  setStepErrorMessage(BLANK_SPACE_ERROR);
                                  setStepError(true);
                                } else if (hasMultipleSpaces(newAnswer)) {
                                  setStepErrorMessage(MULTIPLE_SPACE_ERROR);
                                  setStepError(true);
                                } else {
                                  setStepError(false);
                                  setStepErrorMessage(ANSWER_REQUIRED_ERROR);
                                }
                              }}
                            />
                          </div>
                        </FormControl>
                      </div>
                    </div>
                    <div className="stepper-form-action">
                      <LightButton onClick={prevStep} disabled={currentStep === 1 || disableButton} >
                        <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg" version="1.1" transform="matrix(-1,0,0,1,0,0)">
                          <path d="M13.7063 6.70664C14.0969 6.31602 14.0969 5.68164 13.7063 5.29102L8.70625 0.291016C8.31563 -0.0996094 7.68125 -0.0996094 7.29063 0.291016C6.9 0.681641 6.9 1.31602 7.29063 1.70664L10.5875 5.00039H1C0.446875 5.00039 0 5.44727 0 6.00039C0 6.55352 0.446875 7.00039 1 7.00039H10.5844L7.29375 10.2941C6.90312 10.6848 6.90312 11.3191 7.29375 11.7098C7.68437 12.1004 8.31875 12.1004 8.70938 11.7098L13.7094 6.70977L13.7063 6.70664Z" fill="white"></path>
                        </svg>
                        Previous
                      </LightButton>
                      {stepError && (
                        <div className="step-error" style={{ color: 'red', marginBottom: 8 }}>
                          {stepErrorMessage}
                        </div>
                      )}
                      {currentStep < totalQuestions ? (
                        <PrimaryButton className="next-button" onClick={nextStep}>
                          Next
                          <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13.7063 6.70664C14.0969 6.31602 14.0969 5.68164 13.7063 5.29102L8.70625 0.291016C8.31563 -0.0996094 7.68125 -0.0996094 7.29063 0.291016C6.9 0.681641 6.9 1.31602 7.29063 1.70664L10.5875 5.00039H1C0.446875 5.00039 0 5.44727 0 6.00039C0 6.55352 0.446875 7.00039 1 7.00039H10.5844L7.29375 10.2941C6.90312 10.6848 6.90312 11.3191 7.29375 11.7098C7.68437 12.1004 8.31875 12.1004 8.70938 11.7098L13.7094 6.70977L13.7063 6.70664Z" fill="white" />
                        </svg>
                      </PrimaryButton>
                    ) : (
                          <PrimaryButton className="next-button" type="submit" disabled={disableButton} onClick={handleSubmit} style={{
                            cursor:
                              disableButton ? "not-allowed" : "pointer",
                          }}
                            sx={{
                              "&.Mui-disabled": {
                                color: "#fff",
                              },
                            }}
                          >
                        Submit
                        {disableButton ?(<ButtonLoader/>):(<><svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13.7063 6.70664C14.0969 6.31602 14.0969 5.68164 13.7063 5.29102L8.70625 0.291016C8.31563 -0.0996094 7.68125 -0.0996094 7.29063 0.291016C6.9 0.681641 6.9 1.31602 7.29063 1.70664L10.5875 5.00039H1C0.446875 5.00039 0 5.44727 0 6.00039C0 6.55352 0.446875 7.00039 1 7.00039H10.5844L7.29375 10.2941C6.90312 10.6848 6.90312 11.3191 7.29375 11.7098C7.68437 12.1004 8.31875 12.1004 8.70938 11.7098L13.7094 6.70977L13.7063 6.70664Z" fill="white" />
                          </svg></>)}
                        </PrimaryButton>
                    )}
                  </div>
                </div>
              );
            })}
            </form>
            {!isStudyRelated && <div className="empty-state-wrap">
              <div className="empty-state-block">
                <img src={EmptyStates} alt="" />
                <div className="empty-state-dsc">
                  No study-related prompts found. Kindly ask study related doubts.
                </div>
                <PrimaryButton onClick={() => { navigate('/dashboard') }}>
                  Chat Now
                </PrimaryButton>
              </div>
            </div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExerciseTestStepper;